import { useEffect, useId, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type {
  WorkspaceSecretKind,
  WorkspaceSecretView,
} from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';

import { isSecretError } from '../errors';
import type { SecretErrorField } from '../errors';
import {
  MAX_DESCRIPTION_CHARS,
  MAX_VALUE_BYTES,
  descriptionLength,
  validateDescription,
  validateKey,
  validateValue,
} from '../validation';

/** What a link to the add form may name (spec 240): the AI-Powered SDLC MCP
 *  server sends the person here to type a secret's value outside the chat. */
export interface SecretPrefill {
  key?: string;
  kind?: WorkspaceSecretKind;
  description?: string;
}

export type SecretFormMode =
  | { kind: 'create'; prefill?: SecretPrefill }
  | { kind: 'edit'; entry: WorkspaceSecretView };

/** Past this, a byte count tells the person something they cannot see. Below it,
 *  it is a number nobody asked for next to a 20-character token. */
const COUNTER_THRESHOLD = MAX_VALUE_BYTES / 2;

/** Same fraction, same intent — see `SecretDescriptionSheet`, which shares it. */
const DESCRIPTION_COUNTER_THRESHOLD = MAX_DESCRIPTION_CHARS * 0.8;

const KIND_CHOICES: {
  value: WorkspaceSecretKind;
  label: string;
  consequence: string;
}[] = [
  {
    value: 'secret',
    label: 'Secret',
    consequence: 'Write-only. Cannot be displayed after saving.',
  },
  {
    value: 'variable',
    label: 'Variable',
    consequence: 'Readable, and editable in place later.',
  },
];

export interface SecretFormSubmit {
  key: string;
  kind: WorkspaceSecretKind;
  value: string;
  /** Create only. Absent in edit mode, which does not own this field. */
  description?: string | null;
}

/** What the frame's footer needs to draw its buttons. */
export interface SecretFormFooterState {
  canSubmit: boolean;
  isPending: boolean;
  editing: boolean;
}

/**
 * The secret form itself — kind, name, value, description — with every rule
 * the Secrets page applies, and no frame.
 *
 * Two frames render it (spec 123 R1): `SecretFormSheet` on the Secrets page,
 * and `CreateSecretDialog` over the connection form. One component is what
 * keeps FR-004 true over time — a secret made from a connection form is
 * indistinguishable from one made on the Secrets page because it is made by
 * the same fields and the same validation.
 *
 * The frame supplies `body` (how the fields are wrapped: a scrolling sheet
 * body, a plain column) and `footer` (its own buttons, given what they need
 * to know). Both land *inside* the `<form>`, so the submit button is a real
 * submit rather than one reached through a `form` attribute — the
 * indirection the sheet's earlier comment declined to make load-bearing.
 */
export function SecretForm({
  mode,
  isPending,
  error,
  existingKeys,
  managedKeys,
  connectedTypes,
  onConnect,
  onSubmit,
  onClearError,
  suggestedKey,
  initialKind,
  initialValue,
  initialDescription,
  body,
  footer,
  onDirtyChange,
  className,
}: {
  mode: SecretFormMode;
  isPending: boolean;
  error: unknown;
  /** Every key already in this workspace, so a collision is caught without a
      round trip. */
  existingKeys: string[];
  /** Names a connection owns (from the catalog), keyed by entry name. A match
      is answered with "connect the provider" instead of a create the service
      would refuse (`SECRET_KEY_MANAGED`). */
  managedKeys?: Record<string, { type: string; displayName: string }>;
  /** Provider types already connected, so the hint says "set by" and offers
      Manage instead of telling the reader to connect what already is. */
  connectedTypes?: ReadonlySet<string>;
  onConnect?: (type: string) => void;
  onSubmit: (input: SecretFormSubmit) => void;
  /** Clears the server's refusal once the person edits what it complained
      about. */
  onClearError: () => void;
  /** Seeds the name in create mode; editable. */
  suggestedKey?: string;
  /** Create mode: the kind the form opens with (a host is a variable, not a secret). */
  initialKind?: WorkspaceSecretKind;
  /** Create mode: a value already known, such as a self-hosted instance's host. */
  initialValue?: string;
  /** Create mode: a description already known, such as one an MCP link carried. */
  initialDescription?: string;
  body: (fields: ReactNode) => ReactNode;
  footer: (state: SecretFormFooterState) => ReactNode;
  /** Whether anything differs from what the form opened with — a boolean
      only, so the frame can ask before discarding (spec 129 FR-006) without
      the value ever leaving this component. */
  onDirtyChange?: (dirty: boolean) => void;
  className?: string;
}) {
  const editing = mode.kind === 'edit';
  const entry = editing ? mode.entry : undefined;

  // A suggested key seeds the field in create mode (spec 123 FR-005); it is
  // ordinary typed text from then on, and untouched until the person edits it.
  const [key, setKey] = useState(entry?.key ?? suggestedKey ?? '');
  const [kind, setKind] = useState<WorkspaceSecretKind>(
    entry?.kind ?? initialKind ?? 'secret',
  );
  // A variable's current value is prefilled; a secret's cannot be, because the
  // service returns nothing for one.
  const [value, setValue] = useState(entry?.value ?? initialValue ?? '');
  // Create only. The edit mode is "replace the value" and must not grow a
  // description field (spec 060 FR-019): editing a note and rotating a
  // credential are separate acts, and one form doing both invites the second by
  // accident. Descriptions are edited from the row menu instead.
  const [description, setDescription] = useState(
    editing ? '' : (initialDescription ?? ''),
  );
  const [keyTouched, setKeyTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const dirty =
    key !== (entry?.key ?? suggestedKey ?? '') ||
    kind !== (entry?.kind ?? initialKind ?? 'secret') ||
    value !== (entry?.value ?? initialValue ?? '') ||
    description !== (editing ? '' : (initialDescription ?? ''));
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // No effect re-seeds these when the sheet opens on a different entry: the
  // page gives this component a `key` derived from the mode, so React remounts
  // it and the initialisers above run again. Resetting in an effect would mean
  // an extra render pass with the previous entry's value briefly on screen —
  // and for a secret, "briefly on screen" is the one thing to avoid.

  const kindGroupId = useId();
  const kindLabelId = useId();
  const keyId = useId();
  const keyErrorId = useId();
  const valueId = useId();
  const valueNoteId = useId();
  const valueErrorId = useId();
  const descriptionId = useId();
  const descriptionNoteId = useId();
  const descriptionErrorId = useId();

  const serverError = isSecretError(error) ? error : undefined;
  const genericError =
    error && !serverError ? 'The request failed. Please try again.' : undefined;

  // The mirrored rules. They inform; they never decide (FR-016).
  const managedOwner = editing ? undefined : managedKeys?.[key];
  const ownerConnected = managedOwner
    ? (connectedTypes?.has(managedOwner.type) ?? false)
    : false;
  const localKeyError = editing
    ? null
    : (validateKey(key) ??
      (managedOwner
        ? ownerConnected
          ? `${key} is set by the ${managedOwner.displayName} connection.`
          : `${key} comes from a ${managedOwner.displayName} connection. Connect ${managedOwner.displayName} instead of adding it by hand.`
        : null) ??
      (existingKeys.includes(key)
        ? 'This workspace already has an entry with that name.'
        : null));
  const localValueError = validateValue(value);
  const localDescriptionError = editing
    ? null
    : validateDescription(description);
  const showLocalKeyError = (keyTouched && key.length > 0) || submitted;

  const valueBytes = new TextEncoder().encode(value).length;
  const hasEdgeWhitespace = value.length > 0 && value !== value.trim();

  function messageFor(field: SecretErrorField): string | undefined {
    if (serverError?.field === field) return serverError.message;
    if (field === 'form') return genericError;
    if (field === 'key') {
      return showLocalKeyError ? (localKeyError ?? undefined) : undefined;
    }
    if (field === 'value') {
      return submitted ? (localValueError ?? undefined) : undefined;
    }
    if (field === 'description') {
      return submitted ? (localDescriptionError ?? undefined) : undefined;
    }
    return undefined;
  }

  /** Structural emptiness only.
   *
   * Gating on this is safe because it mirrors nothing — an empty form is not a
   * server rule that could be relaxed later. The mirrored rules (pattern,
   * reserved prefix, size) deliberately do **not** gate: a disabled button
   * carrying a stale copy of a server rule cannot be fixed from the backend,
   * while an enabled one that sends and surfaces the service's answer always
   * degrades correctly. */
  const canSubmit =
    (editing || key.trim().length > 0) && value.length > 0 && !isPending;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    // Guarded rather than relying on a disabled button alone: a double-click or
    // an Enter repeat must not produce two entries (FR-014).
    if (!canSubmit) return;
    // In edit mode the payload carries no `description` key at all, rather than
    // a null. This form does not own the field — descriptions are edited from
    // the row menu (FR-019) — and sending a null would assert something about a
    // field it never showed.
    onSubmit({
      key,
      kind,
      value,
      ...(editing
        ? {}
        : {
            description: description.trim() === '' ? null : description.trim(),
          }),
    });
  }

  const keyMessage = messageFor('key');
  const valueMessage = messageFor('value');
  const descriptionMessage = messageFor('description');
  const formMessage = messageFor('form');

  const fields = (
    <>
      {/* Kind first: the one irreversible choice on this form.
              A div rather than a fieldset so this group shares the exact markup —
              and therefore the exact spacing — of Name and Value below it. The
              grouping semantics come from `role="radiogroup"`. */}
      <div className="flex flex-col gap-1.5">
        <span id={kindLabelId} className="text-sm leading-none font-medium">
          Kind
        </span>
        {editing ? (
          <p className="text-muted-foreground text-sm">
            <span className="font-mono">{entry?.kind}</span> — set when this
            entry was created and fixed for its lifetime.
          </p>
        ) : (
          <div
            role="radiogroup"
            aria-labelledby={kindLabelId}
            className="grid gap-2 sm:grid-cols-2"
          >
            {KIND_CHOICES.map((choice) => (
              <label
                key={choice.value}
                className={cn(
                  'border-border hover:bg-muted/50 flex cursor-pointer flex-col gap-1 rounded-lg border p-3 transition-colors',
                  kind === choice.value && 'border-ring bg-muted/50',
                )}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={kindGroupId}
                    value={choice.value}
                    checked={kind === choice.value}
                    disabled={isPending}
                    onChange={() => {
                      setKind(choice.value);
                    }}
                    className="accent-primary"
                  />
                  <span className="text-sm font-medium">{choice.label}</span>
                </span>
                <span className="text-muted-foreground text-xs">
                  {choice.consequence}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        {/*
                Required only while creating, which is exactly what `canSubmit`
                says: editing leaves the key read-only, and demanding a field
                the form will not let you touch is a mark with no action behind
                it.
              */}
        <div className="flex items-center gap-0.5">
          <Label htmlFor={keyId}>Name</Label>
          {!editing && <RequiredMark />}
        </div>
        <Input
          id={keyId}
          value={key}
          onChange={(event) => {
            setKey(event.target.value);
            setKeyTouched(true);
            if (serverError?.field === 'key') onClearError();
          }}
          disabled={editing || isPending}
          readOnly={editing}
          placeholder="DEPLOY_TOKEN"
          className="font-mono"
          autoFocus={!editing && !suggestedKey}
          aria-invalid={keyMessage ? true : undefined}
          aria-describedby={keyMessage ? keyErrorId : undefined}
        />
        {keyMessage ? (
          <p id={keyErrorId} className="text-destructive text-xs">
            {keyMessage}
          </p>
        ) : null}
        {managedOwner && onConnect ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => {
              onConnect(managedOwner.type);
            }}
          >
            {ownerConnected ? 'Manage' : 'Connect'} {managedOwner.displayName}
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-0.5">
          <Label htmlFor={valueId}>Value</Label>
          <RequiredMark />
        </div>
        {/* One control for both kinds, at one size.
                It was an Input for a secret and a Textarea for a variable, on the
                reasoning that most secrets are single-line tokens. That was wrong
                twice over: toggling the kind resized the field and shifted the
                whole panel under the reader, and plenty of real secrets — a PEM
                private key, a service-account JSON — are multi-line, so the
                single-line control made the harder case the awkward one. The stray
                trailing newline that a textarea invites is already caught by the
                whitespace hint below. */}
        <Textarea
          id={valueId}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            if (serverError?.field === 'value') onClearError();
          }}
          disabled={isPending}
          rows={3}
          className="font-mono"
          // With a suggested name in place the value is the next thing
          // to type, so it takes the cursor (spec 123 FR-004).
          autoFocus={editing || Boolean(suggestedKey)}
          aria-invalid={valueMessage ? true : undefined}
          aria-describedby={valueNoteId}
        />

        {/* Reserved height: the secret note runs to two lines and the
                variable note to one, so without this the footer jumps whenever the
                kind is toggled. */}
        <p id={valueNoteId} className="text-muted-foreground min-h-8 text-xs">
          {editing
            ? entry?.kind === 'secret'
              ? // Without this sentence the empty field reads as a page
                // that failed to load the value, rather than one that
                // never can.
                'The stored value cannot be shown. Whatever you enter here replaces it.'
              : 'This value is readable, so it is shown above and you can edit it in place.'
            : kind === 'secret'
              ? // Said while the form is being filled in — before the
                // one-way door closes, not after (FR-008).
                'Once saved, this value can never be displayed again. You can replace or delete it, but not read it back — keep your own copy if you need one.'
              : 'This value stays readable and can be edited in place later.'}
        </p>

        {/* The classic failure of every secrets store: a newline picked up
                from a terminal or a provider console. The value then looks
                right everywhere and fails every request that uses it. Never
                trimmed silently — values are opaque by contract, and altering
                one behind the person's back would be worse than the bug. */}
        {hasEdgeWhitespace ? (
          <p className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
            <span>
              This value starts or ends with whitespace, which is usually an
              accident from copying.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => {
                setValue(value.trim());
              }}
            >
              Trim it
            </Button>
          </p>
        ) : null}

        {valueBytes > COUNTER_THRESHOLD ? (
          <p className="text-muted-foreground text-xs tabular-nums">
            {Math.round(valueBytes / 1024)} KB of 64 KB
          </p>
        ) : null}

        {valueMessage ? (
          <p id={valueErrorId} className="text-destructive text-xs">
            {valueMessage}
          </p>
        ) : null}
      </div>

      {/* Create only, and last: the name and the kind are what the entry
              *is*, the value is what it carries, and the description is the
              note about it. Optional, and said so — absence is the normal state
              and a required prose field in front of someone pasting a token
              under time pressure produces "api key". */}
      {editing ? null : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={descriptionId}>
            Description{' '}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Input
            id={descriptionId}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              if (serverError?.field === 'description') onClearError();
            }}
            disabled={isPending}
            placeholder="Live key for the billing account, rotates quarterly"
            aria-invalid={descriptionMessage ? true : undefined}
            aria-describedby={
              descriptionMessage ? descriptionErrorId : descriptionNoteId
            }
          />
          {descriptionMessage ? (
            <p id={descriptionErrorId} className="text-destructive text-xs">
              {descriptionMessage}
            </p>
          ) : (
            <p id={descriptionNoteId} className="text-muted-foreground text-xs">
              Anyone with access to this workspace can read this, and every
              agent that runs a step here receives it. Do not put the value
              itself here.
            </p>
          )}
          {/* The same counter the edit sheet shows, on the same
                    threshold. Without it the two surfaces disagreed about when
                    the limit is worth mentioning — you learned it here by being
                    refused, and there by watching a count. */}
          {descriptionLength(description) > DESCRIPTION_COUNTER_THRESHOLD ? (
            <p className="text-muted-foreground text-xs tabular-nums">
              {descriptionLength(description)} of {MAX_DESCRIPTION_CHARS}{' '}
              characters
            </p>
          ) : null}
        </div>
      )}

      {formMessage ? (
        <p role="alert" className="text-destructive text-sm">
          {formMessage}
        </p>
      ) : null}
    </>
  );

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('flex min-h-0 flex-1 flex-col', className)}
    >
      {body(fields)}
      {footer({ canSubmit, isPending, editing })}
    </form>
  );
}
